/**
 * SessionFileManager.ts - SessionFileManager
 * 
 * CHANGE HISTORY:
 * - 251021_0509 [c090082] danielbeer: feat: complete remaining TypeScript fixes across all packages
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
interface SessionFileManagerOptions {
  actr?: any; // Actr instance
  [key: string]: any;
}

export class SessionFileManager {
  private actr: any;
  constructor(options: SessionFileManagerOptions = {}) {
    this.options = {
      sessionsDir: options.sessionsDir || 'docs/sessions',
      historyDir: options.historyDir || 'docs/history',
      autoUpdate: options.autoUpdate !== false,
      ...options
    };
  }
  

  /**
   * Register SessionFile actions with Actr
   */
  private registerActions(): any {
    if (!this.actr) return;
    
    // createSessionFile action
    this.actr.register('loggr:create-session-file', {
      handler: async (params) => {
        return await this.createSessionFile(params);
      },
      description: 'Create session file'
    });

    // updateSessionFile action
    this.actr.register('loggr:update-session-file', {
      handler: async (params) => {
        return await this.updateSessionFile(params);
      },
      description: 'Update session file'
    });
  }

  /**
   * Generate session filename
   * Format: YYYY-MM-DD_sessionId_userId_machineId_description.md
   */
  generateSessionFilename(session, userInfo): any {
    const date = session.startTime.split('T')[0]; // YYYY-MM-DD
    const sessionId = session.id;
    const userId = userInfo?.shortId || userInfo?.id || 'unknown';
    const machineId = userInfo?.machineId ? 
      userInfo.machineId.replace(/[^a-z0-9]+/gi, '-').substring(0, 20) : 
      'unknown-machine';
    const description = session.description
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .substring(0, 30);
    
    return `${date}_${sessionId}_${userId}_${machineId}_${description}.md`;
  }
  
  /**
   * Generate session file content
   */
  generateSessionContent(session, userInfo, activities): any {
    const startTime = new Date(session.startTime);
    const endTime = session.endTime ? new Date(session.endTime) : null;
    const duration = endTime 
      ? Math.round((endTime - startTime) / 1000 / 60)
      : Math.round((Date.now() - startTime) / 1000 / 60);
    
    let content = `---
session_id: ${session.id}
user_id: ${userInfo?.shortId || userInfo?.id || 'unknown'}
user_name: ${userInfo?.fullName || userInfo?.name || 'Unknown User'}
machine_id: ${userInfo?.machineId || 'unknown-machine'}
project: ${session.project || 'unknown-project'}
project_path: ${session.projectPath || 'unknown'}
start_time: ${session.startTime}
end_time: ${session.endTime || 'null'}
duration: ${duration} minutes
type: ${session.type}
status: ${session.endTime ? 'completed' : 'active'}
---

# Development Session: ${session.description}

**Session ID**: ${session.id}  
**User**: ${userInfo?.shortId || userInfo?.id || 'unknown'} (${userInfo?.fullName || userInfo?.name || 'Unknown User'})  
**Machine**: ${userInfo?.machineId || 'unknown-machine'}  
**Project**: ${session.project || 'unknown-project'}  
**Project Path**: \`${session.projectPath || 'unknown'}\`  
**Date**: ${startTime.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}  
**Start**: ${startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} EDT  
**End**: ${endTime ? endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' EDT' : 'Active'}  
**Duration**: ${duration} minutes  
**Type**: ${session.type}

---

## Session Overview

${session.description}

**Project Context**: Working in \`${session.project || 'unknown-project'}\` repository  
**Machine Context**: Running on \`${userInfo?.machineId || 'unknown-machine'}\`

---

## Activities

`;

    // Add activities
    if (activities && activities.length > 0) {
      activities.forEach((activity, index) => {
        const time = new Date(activity.timestamp);
        const timeStr = time.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          timeZone: 'America/New_York'
        });
        
        content += `### ${timeStr} - ${activity.description}\n`;
        content += `- **Type**: ${activity.type}\n`;
        
        if (activity.context && Object.keys(activity.context).length > 0) {
          content += `- **Context**: ${JSON.stringify(activity.context, null, 2)}\n`;
        }
        
        content += `\n`;
      });
    } else {
      content += `No activities logged yet.\n\n`;
    }

    content += `---

## Statistics

- **Duration**: ${duration} minutes
- **Activities**: ${activities ? activities.length : 0}
- **Status**: ${session.endTime ? '✅ Completed' : '🟢 Active'}

---

## Session Metadata

\`\`\`json
{
  "session_id": "${session.id}",
  "user_id": "${userInfo?.shortId || userInfo?.id || 'unknown'}",
  "user_name": "${userInfo?.fullName || userInfo?.name || 'Unknown User'}",
  "machine_id": "${userInfo?.machineId || 'unknown-machine'}",
  "project": "${session.project || 'unknown-project'}",
  "project_path": "${session.projectPath || 'unknown'}",
  "start_time": "${session.startTime}",
  "end_time": ${session.endTime ? `"${session.endTime}"` : 'null'},
  "duration_minutes": ${duration},
  "type": "${session.type}",
  "status": "${session.endTime ? 'completed' : 'active'}",
  "activities": ${activities ? activities.length : 0}
}
\`\`\`

---

**Session Status**: ${session.endTime ? '✅ Completed' : '🟢 Active'}  
**Last Updated**: ${new Date().toISOString()}
`;

    return content;
  }
  
  /**
   * Create session file
   */
  async createSessionFile(session, userInfo, activities): Promise<any> {
    const filename = this.generateSessionFilename(session, userInfo);
    const filepath = `${this.options.sessionsDir}/${filename}`;
    const content = this.generateSessionContent(session, userInfo, activities);
    
    // In browser, can't write files directly
    if (typeof window !== 'undefined') {
      console.log('Session file content generated:', filepath);
      return { filepath, content };
    }
    
    // In Node.js, write to file
    if (typeof require !== 'undefined') {
      try {
        const fs = require('fs');
        const path = require('path');
        
        // Ensure directory exists
        const dir = path.dirname(filepath);
        if (!fs.existsSync(dir)) {
          fs.mkdirSync(dir, { recursive: true });
        }
        
        // Write file
        fs.writeFileSync(filepath, content, 'utf8');
        console.log(`✅ Session file created: ${filepath}`);
        
        return { filepath, content };
      } catch (error) {
        console.error('Failed to create session file:', error);
        return null;
      }
    }
    
    return { filepath, content };
  }
  
  /**
   * Update session file
   */
  async updateSessionFile(session, userInfo, activities): Promise<any> {
    return await this.createSessionFile(session, userInfo, activities);
  }
  
  /**
   * Get session file path
   */
  getSessionFilePath(session, userInfo): any {
    const filename = this.generateSessionFilename(session, userInfo);
    return `${this.options.sessionsDir}/${filename}`;
  }
}

export default SessionFileManager;
